import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { ProgressSteps } from '@/components/ui/ProgressSteps';
import { AnalystChat } from '@/components/profile/AnalystChat';
import { AppProposals } from '@/components/profile/AppProposals';
import { ProfileSkeleton } from '@/components/profile/ProfileSkeleton';
import { QualificationIntro } from '@/components/profile/QualificationIntro';
import { QualificationSheetHeader } from '@/components/profile/QualificationSheetHeader';
import { QualificationSheetView } from '@/components/profile/QualificationSheetView';
import { ThemeCards } from '@/components/profile/ThemeCards';
import { ThemeFocusView } from '@/components/profile/ThemeFocusView';
import { RestitutionView, writingStatus } from '@/components/profile/restitution/RestitutionView';
import { useRestitutionWriting } from '@/components/profile/restitution/useRestitutionWriting';
import { SetupStep } from '@/components/profile/setup/SetupStep';
import { GoalsStep } from '@/components/profile/goals/GoalsStep';
import { loadConnectedProviders } from '@/lib/company-apply';
import { nextSetupItem, setupFinished, setupStages } from '@/lib/company-setup';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import {
  QualificationConflictError,
  countByStatus,
  loadQualification,
  saveQualification,
} from '@/lib/company-qualification';
import {
  type CompanyQualificationSheet,
  type QualificationThemeKey,
  QUALIFICATION_STATUSES,
  QUALIFICATION_THEMES,
} from '@shared/types/company-qualification';
import type { AnalystScope } from '@shared/types/company-analyst';

type Step = 'analysis' | 'objectives' | 'plan';
type Display = Step | 'sheet';

const STEPS = [
  { key: 'analysis', label: 'Analyse' },
  { key: 'objectives', label: 'Objectifs' },
  { key: 'plan', label: 'Mise en place' },
] as const;

/**
 * The company qualification (managers only), as a progression (08/10/2026):
 * ① Analyse (the restitution), ② Objectifs (set from the analysis), ③ Mise en place.
 * Steps show where the work stands but never lock each other. Since 13/10 the
 * analysis is read as one card per theme, each opening its own page (its visual),
 * never as the whole sheet; the proposed applications come first.
 */
export const QualificationTab = ({ requestedStep = null, startAnalysis = false, onBare }: {
  requestedStep?: Step | null;
  /** Opened right after signup: the first analysis starts without « Commencer l’analyse ». */
  startAnalysis?: boolean;
  /** True while loading or on the introduction (no analysis yet): the page hides its tabs. */
  onBare?: (bare: boolean) => void;
}) => {
  const [sheet, setSheet] = useState<CompanyQualificationSheet | null>(null);
  const [version, setVersion] = useState(0);
  const [dropped, setDropped] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [focused, setFocused] = useState<QualificationThemeKey | null>(null);
  const [analystScope, setAnalystScope] = useState<AnalystScope | null>(null);
  const [analystRunning, setAnalystRunning] = useState(false);
  const [mode, setMode] = useState<'read' | 'edit' | null>(null);
  const [display, setDisplay] = useState<Display | null>(null);
  const [theme, setTheme] = useState<QualificationThemeKey | null>(null);
  const [autoStart, setAutoStart] = useState(false);
  const [connected, setConnected] = useState<ReadonlySet<string>>(new Set());
  const onServerSheet = useCallback((next: CompanyQualificationSheet, nextVersion: number) => {
    setSheet(next); setVersion(nextVersion); setDirty(false);
  }, []);
  const { writing, write } = useRestitutionWriting(onServerSheet);
  const onAnalystSheet = useCallback((next: CompanyQualificationSheet, nextVersion: number, closed: boolean) => {
    onServerSheet(next, nextVersion);
    setNotice(`Fiche mise à jour par l’analyste (version ${nextVersion}).`);
    // A closed step gets its restitution, shown as soon as it is written.
    if (closed && analystScope) { setDisplay('analysis'); setTheme(analystScope === 'broad' ? null : analystScope); void write(analystScope); }
  }, [analystScope, onServerSheet, write]);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const stored = await loadQualification();
      setSheet(stored.sheet);
      setVersion(stored.version);
      setDropped(stored.dropped);
      setDirty(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La qualification n’est pas accessible.');
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => { if (requestedStep) setDisplay(requestedStep); }, [requestedStep]);
  const { objectives } = useWorkspaceObjectives();
  const companyObjectives = objectives.filter((goal) => goal.scope === 'company' && goal.status === 'active').length;
  useEffect(() => { void loadConnectedProviders().then(setConnected); }, []);
  const intro = sheet?.broad.state === 'not_started' && !analystScope;
  useEffect(() => {
    if (startAnalysis && intro) { setAutoStart(true); setAnalystScope('broad'); }
  }, [startAnalysis, intro]);
  const bare = intro || (!sheet && !error);
  useEffect(() => { onBare?.(bare); }, [bare, onBare]);
  useEffect(() => () => onBare?.(false), [onBare]);

  const change = (next: CompanyQualificationSheet) => { setSheet(next); setDirty(true); setNotice(null); };

  const save = async (saveNote?: string): Promise<boolean> => {
    if (!sheet) return false;
    setSaving(true);
    setError(null);
    try {
      const saved = await saveQualification(sheet, version, 'manual', saveNote);
      setVersion(saved);
      setDirty(false);
      setNotice(`Version ${saved} enregistrée.`);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'L’enregistrement a échoué.');
      if (cause instanceof QualificationConflictError) setDirty(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Setup changes are saved at once, each with what changed. */
  const commit = async (next: CompanyQualificationSheet, note: string): Promise<boolean> => {
    if (dirty) { setError('Enregistrez d’abord vos modifications de la fiche.'); return false; }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveQualification(next, version, 'manual', note);
      setSheet(next);
      setVersion(saved);
      setNotice(null);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'L’enregistrement a échoué.');
      if (cause instanceof QualificationConflictError) await reload();
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!sheet) {
    return error
      ? <div className="space-y-2"><p className="text-sm text-red-700">{error}</p><Button variant="secondary" size="sm" onClick={() => void reload()}>Réessayer</Button></div>
      : <ProfileSkeleton />;
  }

  const counts = countByStatus(sheet);
  const lineCount = QUALIFICATION_STATUSES.reduce((total, status) => total + counts[status], 0);
  const stages = setupStages(sheet, connected);
  const done: Record<Step, boolean> = {
    analysis: Boolean(sheet.restitution.brief),
    objectives: companyObjectives > 0,
    plan: setupFinished(stages),
  };
  const current: Step = !done.analysis ? 'analysis' : !done.objectives ? 'objectives' : 'plan';
  const shown: Display = display ?? current;
  // The next line to set up, once the analysis and the objectives are there.
  const next = done.analysis && done.objectives ? nextSetupItem(stages) : null;
  const sheetMode = mode ?? (lineCount > 0 ? 'read' : 'edit');
  const deepen = (key: QualificationThemeKey) => { setFocused(key); setAnalystScope(key); setDisplay('analysis'); setTheme(key); };
  const showLines = (key: QualificationThemeKey) => { setDisplay('sheet'); setFocused(key); setMode('read'); };
  const showPage = (key: QualificationThemeKey) => { setDisplay('analysis'); setTheme(key); };
  /** A theme with nothing yet opens the analyst on it; otherwise its page (or its lines). */
  const openTheme = (key: QualificationThemeKey) => {
    const empty = !sheet.restitution.pages[key] && !QUALIFICATION_THEMES.find((entry) => entry.key === key)?.sections.some((section) => sheet.sections[section].length > 0);
    if (empty) deepen(key); else { setDisplay('analysis'); setTheme(key); }
  };
  const openSheet = () => { setDisplay('sheet'); setFocused(null); setTheme(null); };
  // Inside a theme or the detailed sheet, the steps give way to the way back.
  const inside = shown === 'sheet' || (shown === 'analysis' && theme !== null);
  const start = () => { setAutoStart(true); setAnalystScope('broad'); };
  if (intro) return <QualificationIntro onStart={start} />;
  const sheetView = (
    <QualificationSheetView
      sheet={sheet} focused={focused} onFocus={setFocused} mode={sheetMode} onMode={setMode} onChange={change}
      dirty={dirty} saving={saving} analystRunning={analystRunning} error={error} notice={notice}
      onSave={save} onReload={() => void reload()} onDeepen={deepen} onPage={showPage}
    />
  );

  const main = (
    <div className="min-w-0 space-y-5">
      {dropped > 0 && <p className="text-xs text-amber-800">{dropped} ligne{dropped > 1 ? 's' : ''} illisible{dropped > 1 ? 's' : ''} écartée{dropped > 1 ? 's' : ''} à la lecture.</p>}
      {!inside && <ProgressSteps<Step, 'sheet'>
        label="Étapes de la qualification"
        steps={STEPS}
        active={shown}
        current={current}
        done={done}
        onSelect={(step) => { setDisplay(step); setTheme(null); if (step !== 'analysis') setAnalystScope(null); }}
        aside={!analystScope && shown === 'analysis' && (
          <Button size="sm" variant="secondary" onClick={() => setAnalystScope('broad')}>
            {sheet.broad.state === 'done' ? 'Reprendre avec l’analyste' : 'Continuer l’analyse'}
          </Button>
        )}
      />}
      {writing?.running && !theme && <p className="text-sm text-domino-inkSoft">{writingStatus(writing)}</p>}
      {!writing?.running && writing?.error && <p className="text-sm text-red-700">{writing.error}</p>}
      {next && shown !== 'plan' && !inside && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-domino-lilac/60 px-3 py-2 text-sm">
          <span className="font-semibold text-domino-aubergine">Prochaine étape</span>
          <span className="min-w-0 flex-1 truncate text-domino-ink" title={next.title}>{next.title}</span>
          <button type="button" onClick={() => setDisplay('plan')} className="font-medium text-domino-aubergine hover:underline">Y aller</button>
        </div>
      )}
      {error && shown !== 'sheet' && <p className="text-sm text-red-700">{error}</p>}
      {shown === 'analysis' && (theme ? (
        <ThemeFocusView
          sheet={sheet} theme={theme} writing={writing} sheetDirty={dirty}
          onBack={() => setTheme(null)} onDeepen={() => deepen(theme)} onWrite={() => void write(theme)} onLines={() => showLines(theme)}
        />
      ) : (
        <>
          <AppProposals sheet={sheet} />
          {sheet.restitution.brief ? (
            <>
              <RestitutionView sheet={sheet} writing={writing} sheetDirty={dirty} onWrite={() => void write('refresh')} onOpenTheme={openTheme} onOpenSheet={openSheet} />
            </>
          ) : (
            <>
              {sheet.broad.state === 'done' && lineCount > 0 && !writing?.running && (
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => void write('refresh')} disabled={dirty}>Rédiger la restitution</Button>
                </div>
              )}
              <ThemeCards sheet={sheet} onOpen={openTheme} onOpenSheet={openSheet} />
            </>
          )}
        </>
      ))}
      {shown === 'objectives' && <GoalsStep />}
      {shown === 'plan' && <SetupStep sheet={sheet} connected={connected} onCommit={commit} busy={saving || analystRunning} />}
      {shown === 'sheet' && (
        <>
          <QualificationSheetHeader sheet={sheet} onChange={change} onBack={() => setDisplay('analysis')} />
          {sheetView}
        </>
      )}
    </div>
  );
  if (!analystScope) return main;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(360px,460px)_1fr]">
      <div className="lg:sticky lg:top-4 lg:h-[calc(100vh-9rem)] lg:self-start">
        <AnalystChat
          scope={analystScope} sheetDirty={dirty} onSheet={onAnalystSheet} onRunning={setAnalystRunning} onClose={() => { setAnalystScope(null); setAutoStart(false); }}
          closed={analystScope === 'broad' ? sheet.broad.state === 'done' : sheet.themes[analystScope].depth === 'deep'}
          onShowAnalysis={() => { setAnalystScope(null); setDisplay('analysis'); setTheme(null); }} onDeepen={deepen}
          autoStart={autoStart && analystScope === 'broad'}
        />
      </div>
      {main}
    </div>
  );
};
